/**
 * Modal for creating a new list
 */
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import Button from '@/components/Button/Button';
import ColorInput, { DEFAULT_PRESET_COLORS } from '@/components/ColorInput/ColorInput';
import IconSelector from '@/components/IconSelector/IconSelector';
import Modal from '@/components/Modal/Modal';
import { DEFAULT_LIST_ICONS } from '@/types/lists';
import { CreateListSchema } from '@/types/schemas';

import { useCreateList } from '../hooks/api';

export interface CreateListModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after successful list creation with the new list ID */
  onSuccess?: (listId: string) => void;
}

// ============================================================================
// Component
// ============================================================================

export default function CreateListModal({ isOpen, onClose, onSuccess }: CreateListModalProps): ReactElement {
  const [name, setName] = useState('');
  const [selectedIcon, setSelectedIcon] = useState<string>('📋');
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isPending, setIsPending] = useState(false);

  const navigate = useNavigate();
  const createList = useCreateList();

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setSelectedIcon('📋');
      setSelectedColor(null);
      setErrors({});
      setIsPending(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const formData = {
      name: name.trim(),
      icon: selectedIcon,
      color: selectedColor,
    };

    // Validate with Zod
    const result = CreateListSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const path = issue.path[0];
        if (path && typeof path === 'string') {
          fieldErrors[path] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setIsPending(true);
    try {
      const listId = await createList(result.data);
      onSuccess?.(listId);
      onClose();
      // Navigate to the new list
      navigate(`/lists/${listId}`);
    } catch (error) {
      console.error('Failed to create list:', error);
      setErrors({ submit: 'Failed to create list. Please try again.' });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create List" size="sm">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name input */}
        <div>
          <label htmlFor="list-name" className="text-silver mb-1.5 block text-sm font-medium">
            Name <span className="text-danger">*</span>
          </label>
          <input
            id="list-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
            }}
            placeholder="e.g., Cards to Buy, Trade Targets..."
            className="border-storm bg-slate text-pearl placeholder:text-ash focus-ring w-full rounded-lg border px-4 py-2.5 text-sm"
            autoFocus
            maxLength={50}
            disabled={isPending}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'list-name-error' : undefined}
          />
          {errors.name && (
            <p id="list-name-error" className="text-danger mt-1 text-xs">
              {errors.name}
            </p>
          )}
        </div>

        {/* Icon picker */}
        <IconSelector icons={DEFAULT_LIST_ICONS} value={selectedIcon} onChange={setSelectedIcon} label="Icon" disabled={isPending} />

        {/* Color picker */}
        <ColorInput
          value={selectedColor}
          onChange={setSelectedColor}
          presets={DEFAULT_PRESET_COLORS}
          allowCustom={true}
          label="Color"
          optional
          disabled={isPending}
          id="list-color"
        />

        {/* Preview */}
        <div className="bg-slate/50 rounded-lg p-3">
          <p className="text-ash mb-2 text-xs font-medium uppercase">Preview</p>
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg text-lg"
              style={selectedColor ? { backgroundColor: selectedColor } : { backgroundColor: 'var(--color-slate)' }}
            >
              {selectedIcon}
            </div>
            <span className="text-pearl font-medium">{name || 'List Name'}</span>
          </div>
        </div>

        {/* Submit error */}
        {errors.submit && <div className="bg-danger/10 text-danger rounded-lg px-4 py-3 text-sm">{errors.submit}</div>}

        {/* Actions */}
        <div className="border-storm flex justify-end gap-3 border-t pt-4">
          <Button type="button" text="Cancel" variant="text" onClick={onClose} disabled={isPending} />
          <Button
            type="submit"
            text={isPending ? 'Creating...' : 'Create List'}
            color="create"
            onClick={() => {}}
            disabled={!name.trim() || isPending}
          />
        </div>
      </form>
    </Modal>
  );
}
